import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface IndexRow {
  readonly searchIndexId: string;
  readonly indexCode: string;
  readonly sourceModule: string;
  readonly contentType: string;
  readonly title: string;
  readonly classificationLevel: string;
  readonly indexStatus: string;
  readonly versionNo: number;
  readonly popularityScore: number;
  readonly usageCount: number;
}

const MODULES = ['DMS', 'KMS', 'DAM', 'ANALYTICS', 'CONTRACTS', 'CAMPAIGNS', 'VENDORS', 'WORKFLOWS', 'APPROVALS', 'PROJECTS'];
const CONTENT_TYPES = ['DOCUMENT', 'CAMPAIGN', 'CONTRACT', 'VENDOR_RECORD', 'ASSET', 'POLICY', 'SOP', 'KNOWLEDGE_ARTICLE', 'REPORT'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const PAGE = 25;

/**
 * M39 — Index Management Dashboard (§10/§28). The search administrator's surface
 * over the unified index (T514, BR-05: indexes stay synced). Register content for
 * indexing, browse the live index, re-index a single item (bumping its version),
 * soft-delete an entry, or trigger a bulk rebuild for a whole source module.
 * Viewing needs INDEX_VIEW; all mutations need INDEX_MANAGE (SoD, §27). Tenant
 * isolation is enforced server-side (LAW 4); a 403 surfaces the required
 * permission.
 */
export function IndexManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<IndexRow[]>([]);
  const [total, setTotal] = useState(0);
  const [moduleFilter, setModuleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [title, setTitle] = useState('');
  const [sourceEntityId, setSourceEntityId] = useState('');
  const [sourceModule, setSourceModule] = useState('DMS');
  const [contentType, setContentType] = useState('DOCUMENT');
  const [classification, setClassification] = useState('INTERNAL');
  const [rebuildModule, setRebuildModule] = useState('DMS');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (moduleFilter) params.set('sourceModule', moduleFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: IndexRow[]; total: number }>(`/v1/search/index?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, moduleFilter, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m39.indexMgmt.titleRequired')); return; }
    if (!sourceEntityId.trim()) { setActionError(t('m39.indexMgmt.entityRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: IndexRow }>(`/v1/search/index`, {
        method: 'POST',
        body: {
          title: title.trim(),
          sourceEntityId: sourceEntityId.trim(),
          sourceEntityType: `${sourceModule.toLowerCase()}_entity`,
          sourceModule,
          contentType,
          classificationLevel: classification,
        },
        token,
      });
      setNotice(t('m39.indexMgmt.indexed', { code: r.data.indexCode }));
      setTitle('');
      setSourceEntityId('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.indexMgmt.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const reindex = async (row: IndexRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/search/index/${row.searchIndexId}/reindex`, { method: 'POST', token });
      setNotice(t('m39.indexMgmt.reindexed', { code: row.indexCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.indexMgmt.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (row: IndexRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/search/index/${row.searchIndexId}`, { method: 'DELETE', token });
      setNotice(t('m39.indexMgmt.removed', { code: row.indexCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.indexMgmt.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const rebuild = async (): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      const r = await apiRequest<{ data: { rebuilt: number } }>(`/v1/search/index/rebuild`, {
        method: 'POST',
        body: { sourceModule: rebuildModule },
        token,
      });
      setNotice(t('m39.indexMgmt.rebuilt', { n: r.data.rebuilt, module: rebuildModule }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.indexMgmt.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="index-management-dashboard">
      <h1>{t('m39.indexMgmt.title')}</h1>

      <section aria-label="create-index">
        <h2>{t('m39.indexMgmt.registerContent')}</h2>
        <form aria-label="index-form" onSubmit={(e) => void create(e)}>
          <label>{t('m39.common.title')}<input aria-label="field-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m39.indexMgmt.sourceEntityId')}<input aria-label="field-entity" value={sourceEntityId} onChange={(e) => setSourceEntityId(e.target.value)} /></label>
          <label>{t('m39.common.module')}
            <select aria-label="field-module" value={sourceModule} onChange={(e) => setSourceModule(e.target.value)}>
              {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>{t('m39.indexMgmt.contentType')}
            <select aria-label="field-content" value={contentType} onChange={(e) => setContentType(e.target.value)}>
              {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m39.common.classification')}
            <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-index" disabled={busy}>{busy ? t('m39.indexMgmt.indexing') : t('m39.indexMgmt.indexContent')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="index-error">{actionError}</p>}
        {notice && <p aria-label="index-notice">{notice}</p>}
      </section>

      <section aria-label="rebuild-index">
        <h2>{t('m39.indexMgmt.bulkRebuild')}</h2>
        <label>{t('m39.common.module')}
          <select aria-label="rebuild-module" value={rebuildModule} onChange={(e) => setRebuildModule(e.target.value)}>
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <Button variant="primary" type="button" aria-label="submit-rebuild" onClick={() => void rebuild()}>{t('m39.indexMgmt.rebuildModule')}</Button>
      </section>

      <section aria-label="index-filters">
        <label>{t('m39.common.module')}
          <select aria-label="filter-module" value={moduleFilter} onChange={(e) => { setModuleFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m39.common.allModules')}</option>
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="index-total">{t('m39.indexMgmt.total', { total })}</p>
          <DataTable<IndexRow>
            ariaLabel="index-table"
            testId="index-table"
            rowKey={(r) => r.searchIndexId}
            rowDataAttrs={(r) => ({ 'data-index': r.indexCode })}
            emptyMessage={t('m39.common.noIndexedContent')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m39.common.code'), render: (r) => r.indexCode },
              { key: 'title', header: t('m39.common.title'), render: (r) => r.title },
              { key: 'module', header: t('m39.common.module'), render: (r) => r.sourceModule },
              { key: 'type', header: t('m39.common.type'), render: (r) => r.contentType },
              { key: 'status', header: t('m39.common.status'), cellAriaLabel: (r) => `status-${r.indexCode}`, render: (r) => r.indexStatus },
              { key: 'version', header: t('m39.indexMgmt.colVersion'), cellAriaLabel: (r) => `version-${r.indexCode}`, render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m39.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`reindex-${r.indexCode}`} onClick={() => void reindex(r)}>{t('m39.indexMgmt.reindex')}</Button>
                  <Button variant="danger" type="button" aria-label={`remove-${r.indexCode}`} onClick={() => void remove(r)}>{t('m39.indexMgmt.remove')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="index-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m39.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m39.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m39.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
